import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Flex } from 'antd';

import {
  useAppSelector,
  useNavigationToListRequests,
  useRequisitionInfoExistingReq,
} from '@Hooks/index';
import {
  transformDictToOption,
  transformFlagsToOption,
} from '@Utils/transformToOptions';
import { useRespondHrmRequestMutation } from '@Store/api/basicHrmApiSlice';
import { createdUserRequestSelector } from '@Store/selectors/userInfoSelector';
import { getLeaveTypeDictSelector } from '@Store/selectors/dictionarySelector';
import { parseDateTime } from '@Utils/transformDateTime';
import {
  FormApproveRejectRequest,
  HrmRequestFormResponse,
} from '@Types/api/form';
import { FooterActions } from '@Enum/index';
import { NotificationContext } from '@Context/NotificationContext';
import { MainForm } from '@Components/index';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import ApproveList from '@Components/MainForm/components/ApproveList/ApproveList';
import { useGetUserInfoMutation } from '@Store/api/userApiSlice';
import { useFormListMutation } from '@Store/api/dmsApiSlice';
import { getDocument } from '@Features/utils/document';
import { ApproveListData } from '@Components/MainForm/components/ApproveList/types';
import { ReassignWindow } from '@Components/ReassignWindow';
import { EMPTY_FIELD_PLACEHOLDER } from '@Constants';

import { initialValuesForm } from '../initialValues';
import { getDocumentFolder } from '../utils/document';

interface LeaveRequestApproveFormProps {
  formData: HrmRequestFormResponse;
  taskId: string;
}

const LeaveRequestApproveForm: React.FC<LeaveRequestApproveFormProps> = ({
  formData,
  taskId,
}) => {
  const {
    taskApprovedNotification,
    taskErrorNotification,
    taskRejectedNotification,
    errorNotification,
  } = useContext(NotificationContext);
  const { handleBackToListRequests } = useNavigationToListRequests();
  const leaveTypes = useAppSelector(getLeaveTypeDictSelector);
  const [postApproveRejectForm] = useRespondHrmRequestMutation();
  const requestedUser = useAppSelector(createdUserRequestSelector);
  const [comments, setCommentValue] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [isReassignOpen, setReassignOpen] = useState(false);
  const requisitionInfo = useRequisitionInfoExistingReq(formData, 'Leave');
  const [getUser] = useGetUserInfoMutation();
  const [getFormList] = useFormListMutation();

  const formik = useFormik({
    initialValues: initialValuesForm,
    enableReinitialize: true,
    onSubmit: (): void => {},
  });

  useEffect(() => {
    if (!requestedUser || !formData) {
      return;
    }

    if (formData?.replacementEmpNo) {
      getUser({ empNo: formData.replacementEmpNo })
        .unwrap()
        .then((replacementUser) => {
          formik.setValues({
            empNumber: formData.empNumber,
            leaveTypeID: String(
              transformDictToOption(leaveTypes, formData.leaveTypeID).label
            ),
            startDate: parseDateTime(formData.startDate, 'DD/MM/YYYY'),
            endDate: parseDateTime(formData.endDate, 'DD/MM/YYYY'),
            totDays: String(formData.totDays),
            remarks: formData.remarks,
            ticketRequired: formData.ticketRequired,
            passportRequired: formData.passportRequired,
            telephone: formData.telephone || EMPTY_FIELD_PLACEHOLDER,
            replacementEmpNo: formData.replacementEmpNo,
            replacementEmpName: replacementUser.empName,
            replacementEmpDesignation: replacementUser.occupation,
            document: formData.filepath,
            isReplacementRequired: Boolean(formData.isReplacementRequired),
          });
        });
    } else {
      formik.setValues({
        empNumber: formData.empNumber,
        leaveTypeID: String(
          transformDictToOption(leaveTypes, formData.leaveTypeID).label
        ),
        startDate: parseDateTime(formData.startDate, 'DD/MM/YYYY'),
        endDate: parseDateTime(formData.endDate, 'DD/MM/YYYY'),
        totDays: String(formData.totDays),
        remarks: formData.remarks,
        ticketRequired: formData.ticketRequired,
        passportRequired: formData.passportRequired,
        telephone: formData.telephone || EMPTY_FIELD_PLACEHOLDER,
        replacementEmpNo: null,
        replacementEmpName: null,
        replacementEmpDesignation: null,
        document: formData.filepath,
        isReplacementRequired: Boolean(formData.isReplacementRequired),
      });
    }
  }, [formData, requestedUser, leaveTypes]);

  const handleApproveReject = useCallback(
    (action: FooterActions.APPROVE | FooterActions.REJECT): void => {
      const body: FormApproveRejectRequest = {
        taskId: String(taskId),
        command: action,
        comment: comments,
      };

      if (action === FooterActions.REJECT && !comments) {
        errorNotification('Please leave a comment');
        return;
      }

      postApproveRejectForm(body)
        .unwrap()
        .then(() => {
          if (action === FooterActions.APPROVE) {
            taskApprovedNotification();
          } else {
            taskRejectedNotification();
          }

          handleBackToListRequests();
        })
        .catch(() => {
          taskErrorNotification();
        });
    },
    [comments, taskId, handleBackToListRequests]
  );

  useEffect(() => {
    if (!formData?.filepath || !formData?.requestId) {
      return;
    }

    const documentFolderPath = getDocumentFolder(formData.requestId);

    getFormList(documentFolderPath)
      .unwrap()
      .then((response) => {
        const matchedDocument = getDocument(response, formData.filepath);

        if (matchedDocument) {
          setDocumentId(matchedDocument.documentId);
        }
      });
  }, [formData]);

  const dataSourceLeaveDetails = useMemo(() => {
    const documentEl = documentId
      ? {
          label: 'Documents',
          value: formik.values.document,
          extra: {
            document: {
              documentId,
            },
          },
        }
      : null;

    return [
      { label: 'Leave Type', value: formik.values.leaveTypeID },
      { label: 'Start Date', value: formik.values.startDate },
      { label: 'End Date', value: formik.values.endDate },
      { label: 'Total no. of days', value: formik.values.totDays },
      documentEl,
      { label: 'Remarks', value: formik.values.remarks },
      {
        label: 'Ticket Reimbursement Required',
        value: transformFlagsToOption(!!formik.values.ticketRequired).label,
      },
      {
        label: 'Passport Required',
        value: transformFlagsToOption(!!formik.values.passportRequired).label,
      },
      { label: 'Home Country Contact Number', value: formik.values.telephone },
    ].filter((item) => Boolean(item)) as ApproveListData;
  }, [formik.values, documentId]);

  const dataSourceReplacementDetails = useMemo(() => {
    if (
      formik.values.replacementEmpNo &&
      formik.values.replacementEmpName &&
      formik.values.replacementEmpDesignation
    ) {
      return [
        {
          label: 'Replacement Employee Name',
          value: formik.values.replacementEmpName,
        },
        {
          label: 'Replacement Employee Number',
          value: formik.values.replacementEmpNo,
        },
        {
          label: 'Replacement Employee Designation',
          value: formik.values.replacementEmpDesignation,
        },
      ];
    }

    return [];
  }, [formik.values]);

  const handleFooterAction = useCallback(
    (action: FooterActions, payload?: string) => {
      if (action === FooterActions.APPROVE || action === FooterActions.REJECT) {
        handleApproveReject(action);
      }

      if (action === FooterActions.COMMENTS_CHANGE) {
        setCommentValue(payload || '');
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }

      if (action === FooterActions.REASSIGN) {
        setReassignOpen(true);
      }
    },
    [handleBackToListRequests, handleApproveReject]
  );

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={requestedUser}
        requisitionInfo={requisitionInfo}
        onFooterAction={handleFooterAction}
        formType="approval"
      >
        <FormItems>
          <ApproveList
            dataSource={dataSourceLeaveDetails}
            label="Leave Details"
          />
          <ApproveList
            dataSource={dataSourceReplacementDetails}
            label="Replacement Details"
          />
        </FormItems>
      </MainForm>
      <ReassignWindow
        creatorRequestNumber={requestedUser?.empNumber}
        taskId={taskId}
        isOpen={isReassignOpen}
        onClose={() => {
          setReassignOpen(false);
        }}
        onUserSelected={() => {
          setReassignOpen(false);
          handleBackToListRequests();
        }}
      />
    </Flex>
  );
};

export default LeaveRequestApproveForm;
